/**
 * Numéros malgaches.
 *
 * L'API attend du E.164 (`+261341234567`) ; l'utilisateur, lui, tape le numéro
 * comme il le lit sur son téléphone. La conversion se fait ici, dans les deux
 * sens, et nulle part ailleurs.
 *
 * Le module vit dans `shared/` et non dans une feature : un numéro se saisit a
 * l'inscription, sur la fiche d'une boutique, sur un compte de versement et au
 * moment de commander. Ce sont des numéros différents, mais un seul format.
 */
const COUNTRY_CODE = '+261';
const COUNTRY_DIGITS = '261';
/** Longueur nationale malgache, indicatif et zéro initial retirés. */
const NATIONAL_LENGTH = 9;

/** Chiffres nationaux, sans indicatif ni zéro initial : `341234567`. */
export function toNationalDigits(input: string): string {
  const digits = input.replace(/\D/g, '');
  // `00261…` est la forme composée depuis un fixe : c'est le meme numéro.
  const dialed = digits.startsWith('00') ? digits.slice(2) : digits;
  const withoutCountry = dialed.startsWith(COUNTRY_DIGITS)
    ? dialed.slice(COUNTRY_DIGITS.length)
    : dialed;
  return withoutCountry.replace(/^0/, '').slice(0, NATIONAL_LENGTH);
}

/** `34 12 345 67` : le découpage qu'affichent les opérateurs. */
export function formatNational(input: string): string {
  const digits = toNationalDigits(input);
  const groups = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 7), digits.slice(7, 9)];
  return groups.filter(Boolean).join(' ');
}

export function toE164(input: string): string {
  return `${COUNTRY_CODE}${toNationalDigits(input)}`;
}

export function isValidNational(input: string): boolean {
  return toNationalDigits(input).length === NATIONAL_LENGTH;
}

/**
 * Chiffres nationaux d'un numéro **complet**, ou `null` si ce n'est pas un
 * numéro malgache. Sert a l'affichage : un numéro incomplet ou étranger ne doit
 * pas etre découpé comme un `034 …`.
 */
function madagascanNational(value: string): string | null {
  const trimmed = value.trim();
  const digits = trimmed.replace(/\D/g, '');
  const dialed = digits.startsWith('00') ? digits.slice(2) : digits;

  if (dialed.startsWith(COUNTRY_DIGITS)) {
    const national = dialed.slice(COUNTRY_DIGITS.length);
    return national.length === NATIONAL_LENGTH ? national : null;
  }
  // Un autre indicatif, explicitement composé : ce n'est pas le nôtre.
  if (trimmed.startsWith('+')) {
    return null;
  }
  const national = dialed.replace(/^0/, '');
  return national.length === NATIONAL_LENGTH ? national : null;
}

/**
 * Affichage d'un numéro servi par l'API : `+261 34 12 345 67`.
 *
 * Tout ce qui n'est pas un numéro malgache complet (donnée ancienne, ligne
 * étrangere, compte bancaire passé par erreur) est rendu tel quel : lui coller
 * l'indicatif +261 afficherait un autre numéro que le vrai.
 */
export function formatPhone(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const national = madagascanNational(value);
  return national === null ? value.trim() : `${COUNTRY_CODE} ${formatNational(national)}`;
}

/**
 * Coordonnée de versement. Un compte Mobile Money **est** un numéro de
 * téléphone et s'affiche comme tel ; un compte bancaire (RIB, IBAN) n'en est
 * pas un et se lit tel qu'il a été saisi.
 */
export function formatAccountNumber(provider: string, value: string): string {
  return provider === 'BANK' ? value : formatPhone(value);
}

/**
 * Deux numéros désignent-ils la meme ligne ? La comparaison porte sur les
 * chiffres nationaux, jamais sur la frappe : `034 12 345 67` et `+261341234567`
 * sont le meme numéro. Sert a dire au vendeur « c'est déja le numéro de votre
 * boutique » plutot qu'a lui laisser comparer a l'oeil.
 */
export function isSamePhone(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) {
    return false;
  }
  const left = toNationalDigits(a);
  return left.length > 0 && left === toNationalDigits(b);
}
